// Native Shopify purchasing. Keep personal details while native variants update.
const puzzlePropertyState = new Map();

function puzzleFormFor(element) {
  if (!(element instanceof Element)) return null;
  const associatedForm = element instanceof HTMLFormElement ? element : element.form;
  if (associatedForm instanceof HTMLFormElement && associatedForm.closest('mwh-puzzle-purchase')) return associatedForm;
  return element.closest('.mwh-puzzle-product')?.querySelector('mwh-puzzle-purchase form') || null;
}

function puzzleProductKey(form) {
  return form?.closest('product-form-component')?.dataset.productId || null;
}

function personalizationFields(form) {
  return [...form.elements].filter(field =>
    /^(INPUT|TEXTAREA|SELECT)$/.test(field.tagName) &&
    field.name?.startsWith('properties[') &&
    field.type !== 'file' && !field.disabled
  );
}

function rememberPersonalization(form) {
  const key = puzzleProductKey(form);
  if (!key) return;
  const values = new Map();
  for (const field of personalizationFields(form)) {
    if (!values.has(field.name)) values.set(field.name, []);
    if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) continue;
    if (field instanceof HTMLSelectElement && field.multiple) {
      values.get(field.name).push(...[...field.selectedOptions].map(option => option.value));
    } else {
      values.get(field.name).push(field.value);
    }
  }
  puzzlePropertyState.set(key, values);
}

function restorePersonalization(form) {
  const values = puzzlePropertyState.get(puzzleProductKey(form));
  if (!values) return;
  for (const field of personalizationFields(form)) {
    if (!values.has(field.name)) continue;
    const selected = values.get(field.name);
    if (field.type === 'radio' || field.type === 'checkbox') {
      field.checked = selected.includes(field.value);
    } else if (field instanceof HTMLSelectElement && field.multiple) {
      for (const option of field.options) option.selected = selected.includes(option.value);
    } else {
      const value = selected[0] ?? '';
      if (field.value !== value) field.value = value;
    }
  }
}

class MWHPuzzlePurchase extends HTMLElement {
  connectedCallback() {
    // Wait for native form children and externally associated color inputs.
    queueMicrotask(() => {
      if (!this.isConnected) return;
      const form = this.querySelector('form');
      if (!form) return;
      restorePersonalization(form);
      rememberPersonalization(form);
    });
  }
}

if (!customElements.get('mwh-puzzle-purchase')) customElements.define('mwh-puzzle-purchase', MWHPuzzlePurchase);

for (const type of ['input', 'change']) {
  window.addEventListener(type, event => {
    const form = puzzleFormFor(event.target);
    if (form) rememberPersonalization(form);
  }, true);
}

window.addEventListener('variant:selected', event => {
  const form = puzzleFormFor(event.target);
  if (form) rememberPersonalization(form);
}, true);

window.addEventListener('variant:update', event => {
  const productSection = event.target instanceof Element ? event.target.closest('.mwh-puzzle-product') : null;
  if (!productSection) return;
  queueMicrotask(() => {
    const form = productSection.querySelector('mwh-puzzle-purchase form');
    if (form) restorePersonalization(form);
  });
}, true);

// Run before the theme's delegated document-capture submit handler.
function validatePuzzleForm(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const form = event.type === 'submit' ? target : target.closest('button[type="submit"][name="add"]')?.form;
  if (!(form instanceof HTMLFormElement) || !form.closest('mwh-puzzle-purchase')) return;
  // Enter/requestSubmit must also respect unavailable variants and in-flight updates.
  if (form.querySelector('button[type="submit"][name="add"]')?.disabled) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }
  const invalid = [...form.elements].find(field => field.willValidate && !field.validity.valid);
  if (!invalid) {
    rememberPersonalization(form);
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  let parent = invalid.parentElement;
  while (parent && parent !== form) {
    if (parent instanceof HTMLDetailsElement) parent.open = true;
    parent = parent.parentElement;
  }
  requestAnimationFrame(() => {
    invalid.scrollIntoView({ block: 'center', behavior: 'auto' });
    invalid.focus({ preventScroll: true });
    invalid.reportValidity();
  });
}

window.addEventListener('click', validatePuzzleForm, true);
window.addEventListener('submit', validatePuzzleForm, true);
